import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "./login.css";
import Swal from "sweetalert2";
import { isEscapeToInnerPeaceGame } from "./escapeLocalGuestRouting";
import LandingPage from "../escape-to-inner-peace/landing/LandingPage";

function getLoginErrorTitle(message) {
  const normalized = String(message || "").trim().toLowerCase();
  if (normalized === "otp is required for login.") return "Login Error";
  if (normalized.includes("expired")) return "Session Expired!";
  return "Login Failed";
}

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const isLocalEscapeDevHost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const isMissingOtpLocalState =
    !isAdmin &&
    isLocalEscapeDevHost &&
    status === "failed" &&
    String(error || "") === "OTP is required for login.";

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    const desktop = `${process.env.PUBLIC_URL}/images/index_bg.jpg`;
    const mobile = `${process.env.PUBLIC_URL}/images/mob_index.png`;
    const updateBackground = () => {
      setBackgroundImageUrl(window.innerWidth <= 768 ? mobile : desktop);
    };
    updateBackground();
    window.addEventListener("resize", updateBackground);
    return () => window.removeEventListener("resize", updateBackground);
  }, []);

  useEffect(() => {
    if (isMissingOtpLocalState) return;
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
    Swal.fire(getLoginErrorTitle(msg), msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user, isMissingOtpLocalState]);

  useEffect(() => {
    if (isAdmin) return;
    if (status !== "succeeded" || !user) return;
    if (isEscapeToInnerPeaceGame(user.gameId)) return;
    if (Number(user.gameover ?? 0) > 0) {
      Swal.fire("Already Played", "You have already completed this game.", "info").then(() => {
        window.location.href = process.env.REACT_APP_BASE_URL || "/";
      });
    }
  }, [status, user, isAdmin]);

  const isEscapeGuest =
    !isAdmin &&
    status === "succeeded" &&
    user &&
    isEscapeToInnerPeaceGame(user.gameId);

  if (status === "loading" || (themeStatus === "loading" && !isEscapeGuest)) {
    return (
      <div className="login-main-container login-page-background" style={backgroundImageUrl ? { backgroundImage: `url(${backgroundImageUrl})` } : undefined}>
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  if (isMissingOtpLocalState) {
    return (
      <div className="login-main-container login-page-background" style={backgroundImageUrl ? { backgroundImage: `url(${backgroundImageUrl})` } : undefined}>
        <div className="login-local-dev-card" data-testid="login-local-escape-dev-card">
          <p className="login-local-dev-title">Local Escape launcher</p>
          <p className="login-local-dev-copy">
            Localhost does not have a real OTP in the URL, so the normal platform login cannot continue here.
          </p>
          <Link to="/?demo=true&game=escape" className="begin-play-btn" data-testid="login-local-escape-dev-launch">
            Launch Escape Demo
          </Link>
        </div>
      </div>
    );
  }

  if (isEscapeGuest) {
    return <LandingPage />;
  }

  return (
    <div className="login-main-container login-page-background" style={backgroundImageUrl ? { backgroundImage: `url(${backgroundImageUrl})` } : undefined}>
      {themeData && (
        <img
          src={process.env.REACT_APP_S3_PATH + themeData.logo}
          className="login-logo1"
          alt="Game Logo"
        />
      )}
      {user && status === "succeeded" && Number(user.gameover ?? 0) === 0 && (
        <div>
          <Link
            to="/rules/1"
            className="begin-play-btn"
            style={(() => {
              if (!themeData) return undefined;
              const bg = themeData.button_color;
              const fg =
                themeData.button_Textcolor ?? themeData.button_text_color;
              const out = {};
              if (bg != null && String(bg).trim() !== "") {
                out.backgroundColor = String(bg).trim();
              }
              if (fg != null && String(fg).trim() !== "") {
                out.color = String(fg).trim();
              }
              return Object.keys(out).length ? out : undefined;
            })()}
          >
            BEGIN PLAY
          </Link>
        </div>
      )}
    </div>
  );
}

export default Login;
